//@flow
import axios from "axios";
import { enqueueSnackbar } from "notistack";
import React, { useCallback, useMemo } from 'react';
import { Link } from "react-router-dom";
import List from "@mui/material/List";
import { ListSubheader, Switch, ListItemButton, Box } from "@mui/material";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Divider from "@mui/material/Divider";
import { handleAxiosErrors } from "../../../../../helpers/errorHandling";
import { getRouteUrl } from "../../../../../helpers/getRouteUrl";
import { ROUTE_PAGE_EXHIBITION_SETTINGS_PREREGISTRATION_FIELDS, } from "../../../../../routers/routes";
import { Update } from "../../../../../hooks/exhibitions/useExhibitionAction";
import type { Exhibition } from "../../../../../types/exhibition/types";
import { EXHIBITION } from "../../../../../locales/components/namespaces";
import { useTranslation } from "react-i18next";
import TextField from '@mui/material/TextField';
import CopyToClipboard from "../../../../../components/general/copyToClipboard/copyToClipboard";
import { DatePicker } from "@mui/x-date-pickers-pro";
import ImageByLanguage from "../generalExhibitionSettings/components/imageByLanguage";
import PreregistrationGTAG from "./components/preregistrationGTAG";
import PreregistrationHeaderImageLink from "./components/preregistrationHeaderImageLink";
import PreregistrationRedirect from "./components/preregistrationRedirect";
import PreregistrationCountries from "./components/preregistrationCountries";
import PreregistrationCountriesApproval from "./components/preregistrationCountriesApproval";
import { API, PreregistrationRegions, PreregistrationRegionsApproval } from "../../../../../helpers/constants";
import Typography from "@mui/material/Typography";
import { normalizeGreek } from "../../../../../helpers/normalizeGreek";
import useUserData from "../../../../../hooks/user/useUserData";

type Props = {
	id: string,
	updateExhibition: Function,
	exhibition: Exhibition,
}

const PreregistrationSettings = (props: Props) => {
	const { t } = useTranslation(EXHIBITION)
	const { exhibition, updateExhibition, id } = props
	const { dateFormat, token } = useUserData()
	const [value, setValue] = React.useState([exhibition.preregistrationFrom, exhibition.preregistrationTo])
	const togglePreregistrationEnabled = () => {
		updateExhibition(id, !exhibition.preregistrationEnabled, Update.PreregistrationFormEnabled)
	}
	const togglePreregistrationNeedsApproval = (evt) => {
		updateExhibition(id, !exhibition.preregistrationNeedsApproval, Update.PreregistrationNeedsApproval)
		let preregistrationApprovePerCountry = PreregistrationRegionsApproval.NoApproval
		if (evt.target.checked === true) {
			if (exhibition.preregistrationCountry === PreregistrationRegions.AbroadOnly) preregistrationApprovePerCountry = PreregistrationRegionsApproval.AbroadOnly
			if (exhibition.preregistrationCountry === PreregistrationRegions.GreeceOnly) preregistrationApprovePerCountry = PreregistrationRegionsApproval.GreeceOnly
			if (exhibition.preregistrationCountry === PreregistrationRegions.Everywhere) preregistrationApprovePerCountry = PreregistrationRegionsApproval.Everywhere
		}
		updateExhibition(id, preregistrationApprovePerCountry, Update.PreregistrationApprove)
	}
	const togglePreregistrationHasFieldGroups = () => {
		updateExhibition(id, !exhibition.preregistrationHasFieldGroups, Update.PreregistrationHasFieldGroups)
	}
	const updateDates = (newV, index) => {
		const newValue = value.map((v, j) => j === index ? newV : v)
		updateExhibition(id, newValue, Update.PreregistrationDates)
		setValue(newValue)
	}
	
	const onRemovePreregBackground = useCallback((language) => {
		axios({
			method: 'POST',
			headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
			url: `${API}/en/exhibition/deletepreregistrationbg/${id}`,
			data: {
				language: language
			}
		}).then(response => {
			enqueueSnackbar(t('Image removed successfully!'), {
				variant: 'success',
			})
		}).catch(error => {
			enqueueSnackbar(handleAxiosErrors(error, t('Failed to remove image!')), {
				variant: 'error',
			})
		})
	}, [id, t, token])
	
	const preregistrationPublicUrl = useMemo(() => (
		`${exhibition.domainPreregistration}/invite-register?event=${exhibition.id}`
	), [exhibition.domainPreregistration, exhibition.id])
	const preregistrationPreviewUrl = useMemo(() => (
		`${exhibition.domainPreregistration}/invite-register?event=${exhibition.id}&isPreview=true`
	), [exhibition.domainPreregistration, exhibition.id])
	
	const exhibitionIsNotMainOfCoorg = useMemo(() => (Boolean(exhibition.coorganizationId) && !exhibition.isMain), [exhibition.coorganizationId, exhibition.isMain])
	
	return (
		<List
			subheader={<ListSubheader sx={{ backgroundColor: '#F9FBF2' }}><Typography variant={'button'}
			                                                                          sx={{
																						  color: '#000000',
																						  textTransform: 'uppercase'
																					  }}>{normalizeGreek(t('Preregistration'))}</Typography></ListSubheader>}
			sx={{ mb: 1, pb: 0, width: '100%', bgcolor: 'background.paper', border: '1px solid #8c9eff' }}
		>
			<ListItem>
				<ListItemText
					primary={t("Enable preregistration")}
				/>
				<Switch
					edge="end"
					onChange={togglePreregistrationEnabled}
					checked={exhibition.preregistrationEnabled}
				/>
			</ListItem>
			{exhibition.preregistrationEnabled && (
				<CopyToClipboard
					text={preregistrationPublicUrl}
				/>
			)}
			<Divider component="li"/>
			{exhibition.preregistrationEnabled && (
				<CopyToClipboard
					text={preregistrationPreviewUrl}
				/>
			)}
			<Divider component="li"/>
			<PreregistrationCountries
				id={id}
				exhibition={exhibition}
				updateExhibition={updateExhibition}
			/>
			<Divider component="li"/>
			<ListItem
				disabled={!exhibition.preregistrationEnabled}
			>
				<ListItemText
					primary={t("Preregistration Requires Approval")}
					secondary={
						<React.Fragment>
							{t('By enabling this option organizers will have to approve/reject users, after they complete their preregistration.')}
						</React.Fragment>
					}
				/>
				<Switch
					edge="end"
					onChange={togglePreregistrationNeedsApproval}
					checked={exhibition.preregistrationNeedsApproval}
					disabled={!exhibition.preregistrationEnabled}
				/>
			</ListItem>
			<Divider component="li"/>
			<PreregistrationCountriesApproval
				id={id}
				exhibition={exhibition}
				updateExhibition={updateExhibition}
			/>
			<Divider component="li"/>
			<ListItem
				disabled={!exhibition.preregistrationEnabled}
			>
				<ListItemText
					primary={t("Preregistration Dates")}
					secondary={t('Set the date range when preregistration is allowed.')}
				/>
				<Box>
					<DatePicker
						label={t('Start')}
						value={value[0]}
						format={dateFormat}
						onChange={(newValue) => {
							updateDates(newValue, 0);
						}}
						clearable
						sx={{ minWidth: '250px' }}
						slots={{ textField: TextField }}
						slotProps={{
							textField: { size: 'small', variant: 'filled' },
							field: { clearable: true },
						}}
						disabled={!exhibition.preregistrationEnabled}
					/>
					<Box sx={{ my: 1 }}/>
					<DatePicker
						format={dateFormat}
						size={"small"}
						label={t('End')}
						value={value[1]}
						onChange={(newValue) => {
							updateDates(newValue, 1);
						}}
						clearable
						sx={{ minWidth: '250px' }}
						slots={{ textField: TextField }}
						slotProps={{
							textField: { size: 'small', variant: 'filled' },
							field: { clearable: true },
						}}
						disabled={!exhibition.preregistrationEnabled}
					/>
				</Box>
			</ListItem>
			<Divider component="li"/>
			<PreregistrationRedirect
				id={id}
				exhibition={exhibition}
				updateExhibition={updateExhibition}
			/>
			<Divider component="li"/>
			<PreregistrationHeaderImageLink
				id={id}
				exhibition={exhibition}
				updateExhibition={updateExhibition}
			/>
			<Divider component="li"/>
			<PreregistrationGTAG
				id={id}
				exhibition={exhibition}
				updateExhibition={updateExhibition}
			/>
			<Divider component="li"/>
			<ListItem disabled={!exhibition.preregistrationEnabled}>
				<ListItemText
					primary={t("Display Preregistration Form Fields In Groups")}
					secondary={t("If enabled fields of preregistration form are grouped under two groups. You can set a specific group for each field in Field Settings.")}
				/>
				<Switch
					edge="end"
					onChange={togglePreregistrationHasFieldGroups}
					checked={exhibition.preregistrationHasFieldGroups}
					disabled={!exhibition.preregistrationEnabled}
				/>
			</ListItem>
			<Divider component="li"/>
			<ImageByLanguage
				name={'preregistrationBackground'}
				label={t('Prereg. Background Image')}
				updateProperty={'preregistrationBackground'}
				updateType={Update.PreregistrationBg}
				language={"en"}
				displayLanguageOnLabel={false}
				id={id}
				exhibition={exhibition}
				updateExhibition={updateExhibition}
				onRemove={onRemovePreregBackground}
			/>
			<Divider component="li"/>
			<ListItem
				alignItems="flex-start"
			>
				<ListItemButton
					component={Link}
					to={getRouteUrl(ROUTE_PAGE_EXHIBITION_SETTINGS_PREREGISTRATION_FIELDS, { id: id })}
				>
					<ListItemText
						primary={t("Field Settings")}
						secondary={
							<React.Fragment>
								{t('Customize preregistration form layout, enabled-disabled fields etc')}
								{exhibitionIsNotMainOfCoorg && (
									<React.Fragment>
										<br/>
										<Typography variant={'body2'} component={'span'}
										            color={'error'}>{t('This exhibition is part of a coorganization and not the main coorganization exhibition. As such, Field Settings are only used for Pre-Registation Form. To modify Registration Form, visit the main coorganization exhibition settings.')}</Typography>
									</React.Fragment>
								)}
							</React.Fragment>
						}
					/>
				</ListItemButton>
			</ListItem>
		</List>
	)
}

export default PreregistrationSettings;
